import { createContext, useContext, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, LANGUAGE_STORAGE_KEY } from './index';

const LanguageContext = createContext({ language: DEFAULT_LANGUAGE });

export const detectLanguageFromPath = (pathname) => {
    const segment = (pathname || '/').split('/')[1];
    return SUPPORTED_LANGUAGES.includes(segment) ? segment : DEFAULT_LANGUAGE;
};

export const stripLocaleFromPath = (pathname) => {
    if (!pathname) return '/';
    const segment = pathname.split('/')[1];
    if (SUPPORTED_LANGUAGES.includes(segment) && segment !== DEFAULT_LANGUAGE) {
        const stripped = pathname.slice(segment.length + 1);
        return stripped || '/';
    }
    return pathname;
};

export const buildLocalePath = (path, language) => {
    if (!path || typeof path !== 'string') return path;
    if (/^[a-z][a-z0-9+.-]*:/i.test(path)) return path;
    if (!path.startsWith('/')) return path;
    if (language === DEFAULT_LANGUAGE) return path;
    if (path === '/') return `/${language}`;
    return `/${language}${path}`;
};

export const LanguageProvider = ({ children }) => {
    const location = useLocation();
    const { i18n } = useTranslation();
    const language = detectLanguageFromPath(location.pathname);

    useEffect(() => {
        if (i18n.language !== language) {
            i18n.changeLanguage(language);
        }
        if (typeof document !== 'undefined') {
            document.documentElement.lang = language;
        }
        try {
            window.localStorage?.setItem(LANGUAGE_STORAGE_KEY, language);
        } catch (e) {
            // storage may be unavailable (private mode, etc.); fine to ignore
        }
    }, [language, i18n]);

    const value = useMemo(() => ({ language }), [language]);
    return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
};

export const useLanguage = () => useContext(LanguageContext);

export const useLocalizedPath = () => {
    const { language } = useLanguage();
    return useMemo(() => (path) => buildLocalePath(path, language), [language]);
};
